import React, { useEffect, useState } from 'react';

// Demo only (feature flag `role_switcher`): previews the app as each BASYS role.
// usePreviewRole lets role-based views (ROC-102) follow the switch.
export const PREVIEW_ROLES = [
	{ id: 'sales', label: 'Sales' },
	{ id: 'pricing', label: 'Pricing team' },
	{ id: 'manager', label: 'Manager' },
	{ id: 'admin', label: 'Admin' },
] as const;

export type PreviewRole = (typeof PREVIEW_ROLES)[number]['id'];

const STORAGE_KEY = 'rf_preview_role';
const CHANGE_EVENT = 'rf-preview-role';

export const readPreviewRole = (): PreviewRole => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return PREVIEW_ROLES.find((r) => r.id === stored)?.id ?? 'admin';
	} catch {
		return 'admin';
	}
};

export const usePreviewRole = (): PreviewRole => {
	const [role, setRole] = useState<PreviewRole>(readPreviewRole);
	useEffect(() => {
		const update = () => setRole(readPreviewRole());
		window.addEventListener(CHANGE_EVENT, update);
		return () => window.removeEventListener(CHANGE_EVENT, update);
	}, []);
	return role;
};

const RoleSwitcher: React.FC = () => {
	const [role, setRole] = useState<PreviewRole>(readPreviewRole);

	const select = (next: PreviewRole) => {
		setRole(next);
		try {
			localStorage.setItem(STORAGE_KEY, next);
		} catch {
			// Private mode: the switch still works for this page.
		}
		window.dispatchEvent(new Event(CHANGE_EVENT));
	};

	return (
		<div
			role="radiogroup"
			aria-label="Preview as role"
			title="Preview as role"
			className="hidden md:flex h-7 items-center rounded-full border border-neutral-200 bg-white p-0.5"
		>
			{PREVIEW_ROLES.map((r) => (
				<button
					key={r.id}
					type="button"
					role="radio"
					aria-checked={role === r.id}
					onClick={() => select(r.id)}
					className={`h-full rounded-full px-2.5 text-xs font-medium whitespace-nowrap font-primary transition-colors ${
						role === r.id
							? 'bg-violet-100 text-black'
							: 'text-neutral-600 hover:bg-neutral-50'
					}`}
				>
					{r.label}
				</button>
			))}
		</div>
	);
};

export default RoleSwitcher;
